
import { toast } from "sonner";
import { Source } from "@/services/frank/types";

export interface Message {
  id: string;
  text: string;
  sender: "user" | "frank";
  timestamp: Date;
  source?: string;
  sources?: Source[];
}

/**
 * Generates a unique message ID using timestamp and random values
 */
export const generateMessageId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

/**
 * Formats a timestamp for display in message bubbles
 */
export const formatTime = (date: Date): string => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * Creates a new user message object
 */
export const createUserMessage = (text: string): Message => ({
  id: generateMessageId(),
  text,
  sender: "user",
  timestamp: new Date(),
});

/**
 * Creates a new Frank AI response message
 */
export const createFrankMessage = (text: string, source?: string, sources?: Source[]): Message => {
  // Log the sources input to debug
  console.log('Creating Frank message with sources:', JSON.stringify(sources, null, 2));
  
  // Ensure sources is defined and not empty before including it
  const hasValidSources = sources && Array.isArray(sources) && sources.length > 0;
  
  return {
    id: generateMessageId(),
    text,
    sender: "frank",
    timestamp: new Date(),
    source,
    sources: hasValidSources ? sources : undefined
  };
};

/**
 * Creates an error message from Frank when something goes wrong
 */
export const createFrankErrorMessage = (errorText?: string): Message => ({
  id: generateMessageId(),
  text: errorText || "Sorry, I'm having trouble responding right now. Please try again later or contact your system administrator.",
  sender: "frank",
  timestamp: new Date(),
});

/**
 * Handles error processing for Frank API requests
 */
export const handleFrankError = (error: any): { errorMessage: string, shouldUpdateApiStatus: boolean, displayError: string } => {
  console.error("Error asking Frank:", error);
  
  let errorMsg = "Failed to get a response from Frank";
  let shouldUpdateApiStatus = false;
  let displayError = "Sorry, I'm having trouble responding right now. Please try again later.";
  
  // Detect if we're on a mobile device
  const isMobile = window.innerWidth < 768;
  
  if (error.name === "AbortError") {
    if (isMobile) {
      errorMsg = "Request to Frank timed out on mobile device. Try connecting to Wi-Fi or a stronger cellular signal.";
      displayError = "Request timed out. Mobile connections may have limited bandwidth. Try connecting to Wi-Fi.";
    } else {
      errorMsg = "Request to Frank timed out. Please try again.";
      displayError = "Request timed out. The Frank AI service took too long to respond.";
    }
  } else if (error.message && error.message.includes("Network error on mobile device")) {
    // Special handling for mobile network errors
    errorMsg = "Network error on mobile device. Check your connection.";
    displayError = "Network issue detected. Please check your mobile data connection or try Wi-Fi.";
    shouldUpdateApiStatus = false;
  } else if (error.name === "TypeError" && error.message === "Failed to fetch") {
    if (isMobile) {
      errorMsg = "Could not connect to Frank's service from your mobile device. Check your connection.";
      displayError = "Could not connect to Frank. Please check your mobile connectivity or switch to Wi-Fi.";
    } else {
      errorMsg = "Could not connect to Frank's service. The service might be temporarily down or the API URL may be incorrect.";
      displayError = "Could not connect to Frank. Please check your internet connection and the Frank API URL in settings.";
    }
    shouldUpdateApiStatus = true;
  } else if (error.message && error.message.includes("Server error (500)")) {
    errorMsg = "Frank AI service encountered an internal error (500). This is likely a temporary issue with the service.";
    displayError = "Frank AI service encountered an internal error. This is likely a temporary issue.";
    shouldUpdateApiStatus = false;
  } else if (error.message && error.message.includes("API Error: 500")) {
    errorMsg = "Frank AI service returned a 500 error. The service might be experiencing internal issues.";
    displayError = "Frank AI service is having internal issues. Please try again later or check the API URL in settings.";
    shouldUpdateApiStatus = true;
  } else if (error.message && error.message.includes("Invalid association ID format")) {
    errorMsg = "Invalid association ID format. The system uses a string UUID but Frank expects a numeric ID.";
    displayError = "There's a configuration issue with your association ID. Please contact support.";
    shouldUpdateApiStatus = true;
  } else if (error.message && error.message.includes("API Error: 422")) {
    if (error.message.includes("association_id")) {
      errorMsg = "Frank AI service requires a numeric association ID but received a string UUID.";
      displayError = "There's a configuration issue with your association ID format. Please contact support.";
    } else {
      errorMsg = "Frank AI service returned a 422 error. The request data was invalid.";
      displayError = "Invalid request data sent to Frank. Please check the association ID and API settings.";
    }
    shouldUpdateApiStatus = true;
  } else if (error.message) {
    errorMsg = `Error: ${error.message}`;
    displayError = `Error communicating with Frank: ${error.message}`;
  }
  
  toast.error(errorMsg);
  
  return { 
    errorMessage: errorMsg,
    shouldUpdateApiStatus,
    displayError
  };
};

// Default Frank API URL and threshold
const DEFAULT_FRANK_API_URL = "https://dirty-priscella-gapthemind-b863fbd4.koyeb.app";
const DEFAULT_FRANK_THRESHOLD = 0.3; // Already set to 0.3

// Current API version - increment this when changing the default URL to force updates
const CURRENT_API_VERSION = "1.1";

// Get Frank API URL from localStorage or use default
export const getFrankApiUrl = (): string => {
  // Check if we need to update the stored URL
  const storedVersion = localStorage.getItem("frankApiVersion");
  
  // If version doesn't match or doesn't exist, force the new URL
  if (!storedVersion || storedVersion !== CURRENT_API_VERSION) {
    console.log("Updating Frank API URL to new default:", DEFAULT_FRANK_API_URL);
    localStorage.setItem("frankApiUrl", DEFAULT_FRANK_API_URL);
    localStorage.setItem("frankApiVersion", CURRENT_API_VERSION);
    localStorage.setItem("frankThreshold", DEFAULT_FRANK_THRESHOLD.toString());
    toast.info("Frank AI settings have been updated to the latest version", {
      duration: 5000
    });
    return DEFAULT_FRANK_API_URL;
  }
  
  // Otherwise use stored URL or default
  return localStorage.getItem("frankApiUrl") || DEFAULT_FRANK_API_URL;
};

// Get Frank threshold from localStorage or use default, forcing 0.3 regardless of stored value
export const getFrankThreshold = (): number => {
  // Always force the threshold to be the default value
  if (localStorage.getItem("frankThreshold") !== DEFAULT_FRANK_THRESHOLD.toString()) {
    console.log("Forcing Frank threshold to default:", DEFAULT_FRANK_THRESHOLD);
    localStorage.setItem("frankThreshold", DEFAULT_FRANK_THRESHOLD.toString());
  }
  
  return DEFAULT_FRANK_THRESHOLD;
};
